import { EditMode } from "PFApp/booking/types/edit_mode";
import { createContext, useContext } from "react";
import { create, StoreApi, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";

import { BookingEditModeStore, BookingEditModeStoreActions } from "./booking_edit_mode_store.types";
import {
  getInitialSplitModeState,
  INITIAL_EDIT_MODE,
  INITIAL_SELECT_MODE_STATE,
  INITIAL_SMART_PLANNER_MODE_STATE,
  INITIAL_WALL_PLANNER_MODE_STATE
} from "./constants";
import {
  createSelectModeActions,
  createSelectModeSlice,
  createSmartPlannerModeActions,
  createSmartPlannerSlice,
  createSplitModeActions,
  createSplitModeSlice,
  createWallPlannerModeActions,
  createWallPlannerSlice
} from "./slices";

export const createBookingEditModeStore = () =>
  create<BookingEditModeStore>()(
    immer((set, get) => {
      const selectModeActions = createSelectModeActions(set);
      const splitModeActions = createSplitModeActions(set);
      const wallPlannerModeActions = createWallPlannerModeActions(set);
      const smartPlannerModeActions = createSmartPlannerModeActions(set);

      const globalActions: Omit<
        BookingEditModeStoreActions,
        "selectMode" | "splitMode" | "wallPlannerMode" | "smartPlannerMode"
      > = {
        setEditMode: (mode: EditMode) => {
          set((state) => {
            state.editMode = mode;
          });
        },

        resetAll: () => {
          set((state) => {
            state.editMode = INITIAL_EDIT_MODE;
            state.selectMode = INITIAL_SELECT_MODE_STATE;
            state.splitMode = getInitialSplitModeState();
            state.wallPlannerMode = INITIAL_WALL_PLANNER_MODE_STATE;
            state.smartPlannerMode = INITIAL_SMART_PLANNER_MODE_STATE;
          });
        }
      };

      const actions: BookingEditModeStoreActions = {
        ...globalActions,
        selectMode: selectModeActions,
        splitMode: splitModeActions,
        wallPlannerMode: wallPlannerModeActions,
        smartPlannerMode: smartPlannerModeActions
      };

      return {
        editMode: INITIAL_EDIT_MODE,

        ...createSelectModeSlice(set, get, {} as never),
        ...createSplitModeSlice(set, get, {} as never),
        ...createWallPlannerSlice(set, get, {} as never),
        ...createSmartPlannerSlice(set, get, {} as never),

        actions
      };
    })
  );

export type BookingEditModeStoreInstance = ReturnType<typeof createBookingEditModeStore>;

export const BookingEditModeStoreContext = createContext<StoreApi<BookingEditModeStore> | null>(null);

export const useBookingEditModeStoreContext = (): StoreApi<BookingEditModeStore> => {
  const store = useContext(BookingEditModeStoreContext);

  if (!store) {
    throw new Error("useBookingEditModeStoreContext must be used within BookingEditModeStoreProvider");
  }
  return store;
};

export const useBookingEditModeStore = <T>(selector: (state: BookingEditModeStore) => T): T => {
  const store = useBookingEditModeStoreContext();

  return useStore(store, useShallow(selector));
};

export const useBookingEditModeStoreActions = (): BookingEditModeStoreActions => {
  const store = useBookingEditModeStoreContext();

  return useStore(store, (state) => state.actions);
};

export const useEditMode = () => useBookingEditModeStore((state) => state.editMode);

export const useSelectModeState = () => useBookingEditModeStore((state) => state.selectMode);

export const useSplitModeState = () => useBookingEditModeStore((state) => state.splitMode);

export const useWallPlannerModeState = () => useBookingEditModeStore((state) => state.wallPlannerMode);
